import React, { useMemo, useState } from 'react';
import { useCountries } from '@/api/Countries';
import { useServiceProviders } from '@/api/ServiceProviders';
import {
  useServiceUnits,
  useCreateServiceUnit,
  useUpdateServiceUnit,
} from '@/api/ServiceUnits';
import { useServiceUnitTypes } from '@/api/ServiceUnitTypes';
import { ServiceUnit } from '@/types';
import { CustomColumns, CustomTable, faEdit, Icon } from '@/ui/components';
import { Button, Switch } from 'antd';
import { useAppCustomers } from '@/hooks';
import { useTranslate } from '@/translations';
import { UnitPopup } from '../UnitPopup';

export const TransportUnitsDataGrid: React.FC = () => {
  const { t } = useTranslate();
  const {
    userPermissions: { customers },
    hasGbaAdminAccess,
  } = useAppCustomers();

  const {
    data: serviceUnits,
    isFetching: isServiceUnitsLoading,
    refetch,
  } = useServiceUnits();
  const { mutateAsync: createMutate } = useCreateServiceUnit();
  const { mutateAsync: updateMutate } = useUpdateServiceUnit();

  const { data: countries, isFetching: isCountriesLoading } = useCountries();
  const { data: serviceUnitTypes, isFetching: isServiceUnitTypesLoading } =
    useServiceUnitTypes();
  const { data: serviceProviders, isFetching: isServiceProvidersLoading } =
    useServiceProviders();

  const [unit, setUnit] = useState<ServiceUnit>();

  const onSubmit = async (data: ServiceUnit) => {
    const res = await (data.id ? updateMutate(data) : createMutate(data));

    if (res.success) refetch();

    return res.message?.Message;
  };

  const isLoading =
    isServiceUnitsLoading ||
    isCountriesLoading ||
    isServiceUnitTypesLoading ||
    isServiceProvidersLoading;

  const columns: CustomColumns<ServiceUnit> = useMemo(
    () => [
      {
        dataIndex: 'customerName',
        title: 'Customer',
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'ref',
        title: 'Unit Ref',
        filter: true,
        sortable: true,
      },
      { dataIndex: 'name', title: 'Unit Name', filter: true, sortable: true },
      {
        dataIndex: 'countryId',
        title: 'Country Code',
        renderType: 'select',
        render: (_, data) => data.country,
        filter: true,
        filterOptions: countries?.map((o) => ({ value: o.id, text: o.name })),
        sortable: true,
      },
      {
        dataIndex: 'serviceUnitTypeId',
        title: 'Unit Type',
        renderType: 'select',
        render: (_, data) => data.serviceUnitType,
        filter: true,
        filterOptions: serviceUnitTypes?.map((o) => ({
          value: o.id,
          text: o.name,
        })),
        sortable: true,
      },
      {
        dataIndex: 'serviceProviderId',
        title: 'Provider',
        renderType: 'select',
        render: (_, data) => data.serviceProvider,
        filter: true,
        filterOptions: serviceProviders?.map((o) => ({
          value: o.id,
          text: o.name,
        })),
        sortable: true,
      },
      { dataIndex: 'imo', title: 'IMO', filter: true, sortable: true },
      { dataIndex: 'mmsi', title: 'MMSI', filter: true, sortable: true },
      {
        dataIndex: 'isActive',
        title: 'Active',
        render: (value) => <Switch checked={value} />,
      },
      {
        dataIndex: 'id',
        render: (_, data) =>
          hasGbaAdminAccess ? (
            <Button onClick={() => setUnit({ ...data })}>
              <Icon icon={faEdit} /> {t('edit')}
            </Button>
          ) : null,
        width: 150,
      },
    ],
    [countries, hasGbaAdminAccess, serviceProviders, serviceUnitTypes, t],
  );

  return (
    <>
      <CustomTable
        dataSource={serviceUnits || []}
        loading={isLoading}
        columns={columns}
        rowKey="id"
        onAdd={() => setUnit({} as ServiceUnit)}
      />

      <UnitPopup
        data={unit}
        countries={countries || []}
        serviceUnitTypes={serviceUnitTypes || []}
        serviceProviders={serviceProviders || []}
        customers={
          customers?.map((o) => ({
            label: o.customerName,
            value: o.customerId,
          })) || []
        }
        onCancel={() => setUnit(undefined)}
        onSubmit={onSubmit}
      />
    </>
  );
};
